import { useState, useEffect, useCallback } from 'react';
import { 
  getUserSave, 
  createSave, 
  updateSave
} from '../services/gameData';

/**
 * Hook for managing game data
 * @param {string} userId - User ID from Supabase
 * @returns {Object} Game data state and methods
 */
export function useGameData(userId) {
  const [gameSave, setGameSave] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastIncomeUpdate] = useState(null);
  const [gameConstants, setGameConstants] = useState(null);
  
  // Load user's save
  const loadSave = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return null;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { save, constants, error } = await getUserSave(userId);
      
      if (error) {
        setError(error.message || 'Failed to load game save');
        setIsLoading(false);
        return null;
      }
      
      // Store game constants returned from the backend
      if (constants) {
        setGameConstants(constants);
      }
      
      // If a save was found, attach the constants to it
      if (save) {
        save.constants = constants;
      }
      
      setGameSave(save);
      setIsLoading(false);
      
      return save;
    } catch (e) {
      console.error('Error loading save:', e);
      setError(e.message || 'Unexpected error loading save');
      setIsLoading(false);
      return null;
    }
  }, [userId]);
  
  // Create a new save
  const createNewSave = useCallback(async (companyName) => {
    if (!userId) {
      setError('User not authenticated');
      return null;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { save, constants, error } = await createSave(userId, companyName);
      
      if (error) {
        // If user already has a save, load it instead
        if (error === 'User already has a save game' && save) {
          // Update game constants if they were returned
          if (constants) {
            setGameConstants(constants);
            save.constants = constants;
          }
          
          setGameSave(save);
          setIsLoading(false);
          return save;
        }
        
        setError(error.message || 'Failed to create game save');
        setIsLoading(false);
        return null;
      }
      
      // Update game constants if they were returned
      if (constants) {
        setGameConstants(constants);
        save.constants = constants;
      }
      
      setGameSave(save);
      setIsLoading(false);
      return save;
    } catch (e) {
      console.error('Error creating save:', e);
      setError(e.message || 'Unexpected error creating save');
      setIsLoading(false);
      return null;
    }
  }, [userId]);
  
  // Update game data
  const updateGameData = useCallback(async (newData) => {
    if (!gameSave || !gameSave.id) {
      setError('No active game save');
      return false;
    }
    
    if (!newData) {
      setError('No game data provided');
      return false;
    }
    
    setIsLoading(true);
    
    try {
      const { save, constants, error } = await updateSave(gameSave.id, newData);
      
      if (error) {
        setError(error.message || 'Failed to update game data');
        setIsLoading(false);
        return false;
      }
      
      // Update game constants if they were returned
      if (constants) {
        setGameConstants(constants);
        save.constants = constants;
      } else if (gameConstants) {
        // Preserve existing constants if none were returned
        save.constants = gameConstants;
      }
      
      setGameSave(save);
      setIsLoading(false);
      return true;
    } catch (e) {
      console.error('Error updating game data:', e);
      setError(e.message || 'Unexpected error updating game data');
      setIsLoading(false);
      return false;
    }
  }, [gameSave, gameConstants]);
  
  // Buy a mining rig - this is now handled directly by the updateMiningRigs endpoint
  // and called from MiningRigMenu.js
  
  // Load save on mount and when userId changes
  useEffect(() => {
    if (userId) {
      loadSave();
    }
  }, [userId, loadSave]);

  // Update money every 2 minutes if connected
  useEffect(() => {
    if (!userId || !gameSave) return;

    const updateInterval = setInterval(async () => {
      try {
        const { save, constants, error } = await getUserSave(userId);
        if (error) {
          console.error('Error updating game data:', error);
          return;
        }

        if (constants) {
          setGameConstants(constants);
          save.constants = constants;
        }

        setGameSave(save);
      } catch (e) {
        console.error('Error in game data update interval:', e);
      }
    }, 2 * 60 * 1000); // 2 minutes in milliseconds

    return () => clearInterval(updateInterval);
  }, [userId, gameSave?.id]);
  
  return {
    gameSave,
    isLoading,
    error,
    loadSave,
    createNewSave,
    updateGameData,
    lastIncomeUpdate,
    hasSave: !!gameSave,
    gameConstants
  };
} 